/**
 * api/endpoints.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Auth & identity URLs for SK Queue. Same model as every SK product: the app
 * does NOT own login — the SK Platform owns the only auth screens, and the
 * Backbone hub fronts /v1/auth/* and /v1/me, proxying to skplatform-api and
 * relaying the platform's httpOnly Set-Cookie back through the chain. So the
 * request/response contract here is byte-for-byte the platform's; only the
 * transport (the Backbone) differs. (See sk-bridge / catalog: ALL platform
 * comms go through the Backbone.)
 *
 * Product API URLs (queues, entries, read-models) live in api/queueEndpoints.ts.
 * ──────────────────────────────────────────────────────────────────────────
 */

declare const process: { env?: { BACKBONE_API_URL?: string } };

function readBackbone(): string | undefined {
  try { return process.env?.BACKBONE_API_URL; } catch { return undefined; }
}

// Backbone hub base (auth/identity, proxied to the platform). Default = local
// Backbone dev port (:5001), overridable via BACKBONE_API_URL at build time.
export const BACKBONE_API = (readBackbone() || 'http://localhost:5001').replace(/\/$/, '');

// Versioned auth/identity prefix (Backbone → platform v1).
const P = `${BACKBONE_API}/v1`;

/* ─── Platform auth & identity (httpOnly cookie session) ─────────────────── */
export const Platform_Login_Url = `${P}/auth/login`;               // POST { email, password, product }
export const Platform_Login_Mfa_Url = `${P}/auth/login/mfa`;           // POST { mfa_token, code }
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`;             // POST — silent rotation
export const Platform_Logout_Url = `${P}/auth/logout`;              // POST — clears session cookies
export const Platform_Me_Url = `${P}/me`;                       // GET  — identity probe (200/401)
export const Platform_Switch_Org_Url = `${P}/auth/switch-org`;          // POST { org_id }

/* ─── Public guest/door bootstrap (no login) ─────────────────────────────────
 * A public surface (door display / guest QR) has no session; the Backbone mints
 * a short-lived, narrowly-scoped guest token from the public link's (org, slug).
 * The browser holds only this token — never a platform secret. */
export const Queue_Guest_Token_Url = `${P}/queue/guest-token`;          // POST { org, slug } → { token, queue }
